import { useState, useMemo, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { ProductSearchResult, SIGNIFY_CONFIG } from '#types/SignifyApi.types';
import { searchProducts } from '../services/signifyApiService';

const DEBOUNCE_MS = SIGNIFY_CONFIG.searchDebounceMs;
const DEFAULT_PAGE_SIZE = SIGNIFY_CONFIG.defaultPageSize;
const DEFAULT_FIELDS = SIGNIFY_CONFIG.defaultFields;

/**
 * Custom hook for searching Signify products
 * Provides debounced search functionality with loading state
 * Automatically cancels pending searches on unmount
 * @returns Object containing search results, loading state, and utility functions
 */
export const useSignifyProductSearch = ({
    configurable
}: {
    configurable?: 'true' | 'false' | '';
} = {}) => {
    const [productOptions, setProductOptions] = useState<ProductSearchResult[]>([]);
    const [searchingProducts, setSearchingProducts] = useState(false);

    /**
     * Debounced product search function
     * Minimum query length is 3 characters
     * Automatically clears results for invalid queries
     * @returns Debounced search function that updates productOptions state
     */
    const searchProductsDebounced = useMemo(() =>
        debounce(async (searchQuery: string) => {
            if (!searchQuery || searchQuery.length < 3) {
                setProductOptions([]);
                return;
            }

            setSearchingProducts(true);

            const response = await searchProducts({
                query: searchQuery,
                pageSize: DEFAULT_PAGE_SIZE,
                currentPage: 0,
                fields: DEFAULT_FIELDS,
                configurable
            });

            setProductOptions(response.products ?? []);
            setSearchingProducts(false);
        }, DEBOUNCE_MS)
    , [configurable]);

    useEffect(() => {
        return () => {
            searchProductsDebounced.cancel();
        };
    }, [searchProductsDebounced]);

    /**
     * Clear search results
     */
    const clearSearch = useCallback(() => {
        setProductOptions([]);
    }, []);

    return {
        productOptions,
        searchingProducts,
        searchProducts: searchProductsDebounced,
        clearSearch
    };
};